// The GraphQL pages agents may read through this browser: the relay worker
// (relay-serve.ts) keeps one GraphqlBudget for every tab of every user, and a
// tab asks it before each GraphQL page it reads for an agent (relayTools.ts's
// `spend`). Each pairing reads at most `perConnection` pages, and a user's
// pairings together at most `total`, in any `windowMs`: an agent asking in a
// loop, or several agents and tabs at once, can't spend the GitHub budget the
// person's own pages read from (5,000 points an hour).
//
// Persistence: what each user's pairings read is kept in the user's relay
// database (RELAY_BUDGET_STORE, key BUDGET_KEY), pruned to the window, so a
// worker that crashed or ended with the last tab starts again from it. Each
// `spend` reads the log, counts the page and writes it back holding a Web Lock
// on the user's log, so two workers at once (tabs loaded before and after a
// deploy) never write over what the other counted. A page is written there
// before it is granted: a write that hasn't landed has granted nothing, so a
// crash can lose a refused or unanswered page but never hand out one the log
// doesn't hold. A log that can't be read or written, or that takes longer than
// STORE_MS, refuses the page (a write that failed still counts it here). Spends
// of one user are taken one at a time. The log goes with the database: at
// sign-out and at an identity reset (the person's own acts).
import { read, write } from './relay-db';

/** The limits (see the file comment). Tests narrow them. */
export const GRAPHQL_BUDGET = { perConnection: 150, total: 300, windowMs: 10 * 60_000 };
/** Where the log is kept in the user's relay database. */
export const BUDGET_KEY = 'graphql-budget';

/** A page refused for want of budget: a paged answer then goes with what it has read. */
export class BudgetSpent extends Error {}

/** What a user's pairings read within the window: when, oldest first, in all and per pairing. */
export interface BudgetLog { total: number[]; pairings: Record<string, number[]> }
/** Where each user's log is kept between workers, and shared by every worker at once. */
export interface BudgetStore {
  load(user: string): Promise<unknown>;
  save(user: string, log: BudgetLog): Promise<void>;
  /**
   * Runs `fn` holding `user`'s log against every other worker over this
   * store (tabs loaded before and after a deploy run different workers), so
   * a spend's load and save are one step. Gives up waiting when `signal`
   * aborts, rejecting.
   */
  exclusive<T>(user: string, fn: () => Promise<T>, signal: AbortSignal): Promise<T>;
}
/** The user's relay database (relay-db.ts), held through a Web Lock every worker of this origin shares. */
export const RELAY_BUDGET_STORE: BudgetStore = {
  load: user => read<unknown>(user, BUDGET_KEY),
  save: (user, log) => write(user, BUDGET_KEY, log),
  exclusive: (user, fn, signal) => navigator.locks.request(`remote-relay-budget:${user}`, { signal }, fn),
};

/** What a page refused because the log couldn't be kept says. */
export const UNRECORDED = 'Changeling could not record this read from GitHub, so it was not made. Ask again.';
/** How long a spend waits on the store (its lock, the read and the write) before it refuses the page as UNRECORDED. */
export const STORE_MS = 5_000;

const minutes = (ms: number) => `${ms / 60_000} minutes`;
const times = (v: unknown): number[] => Array.isArray(v) ? v.filter((t): t is number => typeof t === 'number' && Number.isFinite(t)).sort((a, b) => a - b) : [];
/** A stored log, whatever was stored: anything else reads as none read. */
function decodeLog(raw: unknown): BudgetLog {
  const r = raw as { total?: unknown; pairings?: unknown } | null;
  const pairings: Record<string, number[]> = {};
  if (r && typeof r.pairings === 'object' && r.pairings !== null) for (const [k, v] of Object.entries(r.pairings)) pairings[k] = times(v);
  return { total: times(r?.total), pairings };
}
const copy = (log: BudgetLog): BudgetLog => ({ total: [...log.total], pairings: Object.fromEntries(Object.entries(log.pairings).map(([k, v]) => [k, [...v]])) });

export class GraphqlBudget {
  /** Without a store, each user's log; with one, the store holds it and each spend reads it anew. */
  private logs = new Map<string, BudgetLog>();
  /**
   * Pages refused because their write failed or wasn't known to land, per
   * user: still counted, in each log read after, until a write holds them.
   */
  private unsaved = new Map<string, { connection: string; at: number }[]>();
  /** Each user's spends in this worker, one at a time. */
  private chains = new Map<string, Promise<unknown>>();
  /** Bumped by `forget`: a spend under way then keeps nothing. */
  private generations = new Map<string, number>();
  constructor(
    private limits: { perConnection: number; total: number; windowMs: number } = GRAPHQL_BUDGET,
    private store?: BudgetStore,
    /** How long a spend waits on the store (tests narrow it). */
    private storeMs = STORE_MS,
  ) {}

  /** Counts one page of `connection` at `now`; or, refusing it, says why and when to ask again. */
  spend(user: string, connection: string, now: number): Promise<string | null> {
    // A spend asked before `forget` keeps nothing, however long it waited its turn.
    const gen = this.generations.get(user) ?? 0;
    const run = (this.chains.get(user) ?? Promise.resolve()).catch(() => {}).then(() => this.take(user, connection, now, gen));
    this.chains.set(user, run);
    return run;
  }

  private async take(user: string, connection: string, now: number, gen: number): Promise<string | null> {
    const stale = () => (this.generations.get(user) ?? 0) !== gen;
    if (stale()) return UNRECORDED;
    const store = this.store;
    if (!store) {
      let log = this.logs.get(user);
      if (!log) this.logs.set(user, log = { total: [], pairings: {} });
      return this.count(log, connection, now);
    }
    // Read, counted and written holding the log against every other worker,
    // so none writes over a page another counted since; bounded, so a store
    // that hangs refuses the page rather than outlast the answer's time.
    const ctl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<string>(resolve => { timer = setTimeout(() => { ctl.abort(); resolve(UNRECORDED); }, this.storeMs); });
    // Each step in the lock gives up with the time, so the lock goes with it
    // and the next spend (here or in another worker) goes on. A read or write
    // left behind still runs; the relay database's transactions on the log
    // take their turns, so the next spend's read comes after it.
    const gone = new Promise<never>((_, reject) => ctl.signal.addEventListener('abort', () => reject(ctl.signal.reason), { once: true }));
    gone.catch(() => {});
    const within = <T>(step: Promise<T>) => Promise.race([step, gone]);
    const counted = store.exclusive(user, async () => {
      if (stale() || ctl.signal.aborted) return UNRECORDED;
      let log: BudgetLog;
      try { log = decodeLog(await within(store.load(user))); } catch { return UNRECORDED; }
      if (stale() || ctl.signal.aborted) return UNRECORDED;
      const kept = (this.unsaved.get(user) ?? []).filter(p => now - p.at < this.limits.windowMs);
      for (const { connection: c, at } of kept) { log.total.push(at); (log.pairings[c] ??= []).push(at); }
      log.total.sort((a, b) => a - b);
      const refused = this.count(log, connection, now);
      if (refused !== null) return refused;
      // Counted from here: a write that fails, or isn't known to land in time, still counts it.
      const page = { connection, at: now };
      this.unsaved.set(user, [...kept, page]);
      const saved = store.save(user, copy(log));
      // Landed, even after the time ran out: the log holds them (the page itself was refused then).
      const landed = () => { if (!stale()) this.unsaved.set(user, (this.unsaved.get(user) ?? []).filter(p => p !== page && !kept.includes(p))); };
      saved.then(landed, () => {});
      try { await within(saved); } catch { return UNRECORDED; }
      return stale() || ctl.signal.aborted ? UNRECORDED : null;
    }, ctl.signal).catch(() => UNRECORDED);
    try { return await Promise.race([counted, late]); } finally { clearTimeout(timer); }
  }

  /** Prunes `log` to the window and counts one page of `connection` at `now` in it; or, refusing it, says why. */
  private count(log: BudgetLog, connection: string, now: number): string | null {
    const { perConnection, total, windowMs } = this.limits;
    const recent = (list: number[]) => list.filter(t => now - t < windowMs);
    log.total = recent(log.total);
    for (const k of Object.keys(log.pairings)) { log.pairings[k] = recent(log.pairings[k]); if (!log.pairings[k].length) delete log.pairings[k]; }
    const mine = log.pairings[connection] ?? [];
    const again = (list: number[]) => {
      const wait = Math.max(1, Math.ceil((list[0] + windowMs - now) / 60_000));
      return `Ask again in ${wait} minute${wait === 1 ? '' : 's'}.`;
    };
    if (mine.length >= perConnection) return `This agent has read ${perConnection} pages from GitHub in the last ${minutes(windowMs)} through Changeling, as many as it may. ${again(mine)}`;
    if (log.total.length >= total) return `Your agents have read ${total} pages from GitHub in the last ${minutes(windowMs)} through Changeling, as many as they may. ${again(log.total)}`;
    log.pairings[connection] = [...mine, now];
    log.total.push(now);
    return null;
  }

  /** Forgets what `user`'s pairings read (signed out, or the identity reset: the database goes too). */
  forget(user: string): void {
    this.generations.set(user, (this.generations.get(user) ?? 0) + 1);
    this.logs.delete(user);
    this.unsaved.delete(user);
    this.chains.delete(user);
  }
}
